import { cn } from "@corpuscomplete/web-ui/lib/utils";
import type { ReactNode } from "react";
import { useCallback, useEffect, useRef } from "react";
import type { UIMessage } from "ai";
import { AgentChat } from "@corpuscomplete/core-ui/ai/agent/chat";
import { AgentTools } from "@corpuscomplete/core-ui/ai/agent/tools";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupTextarea,
} from "@corpuscomplete/web-ui/components/input-group";
import { StopCircleIcon } from "lucide-react";
import { StreamingIndicator } from "./streaming-indicator";

/**
 * Shared chat surface for agent sessions.
 *
 * Boundary:
 * - Presentation only (layout, list rendering, input visibility).
 * - Pending-tool policy is delegated to core-ui (`AgentTools.buildPendingDeferredActionState`).
 * - Input is hidden whenever pending actions exist; stop affordance remains available when allowed.
 */
export function ChatView<TMessage extends UIMessage>({
  className,
  renderUIMessage,
  renderPendingAction,
  deferredToolPresenter,
}: {
  renderUIMessage: (message: AgentChat.UIMessageWithStatus<TMessage>) => ReactNode;
  renderPendingAction: (
    part: AgentTools.DeferredTool,
    info: AgentTools.PendingActionRenderInfo,
  ) => ReactNode;
  deferredToolPresenter?: AgentTools.DeferredToolPresenter;
  className?: string;
}) {
  const { messages, isLoading } = AgentChat.useMessages();
  const lastAssistantMessage = AgentChat.useLastAssistantMessage();
  const { pendingParts, isStreaming, hasPendingActions } = AgentChat.usePendingToolState();
  const isToolPartPersisted = AgentChat.useIsToolPartPersisted();

  const { input, setInput, submit, disabled } = AgentChat.useInput();
  const { stop, canStop = true } = AgentChat.useStop();
  const scrollViewRef = useRef<HTMLDivElement>(null);

  const { pendingDeferredToolQueue, hasRenderablePendingActions, showPendingFallback, showStop } =
    AgentTools.buildPendingDeferredActionState({
      pendingParts,
      hasPendingActions,
      isStreaming,
      deferredToolPresenter: deferredToolPresenter ?? {
        isDeferredToolPart: () => true,
      },
      lastAssistantMessageId: lastAssistantMessage?.id,
      isToolPartPersisted,
    });

  const pendingActionRenderList =
    hasRenderablePendingActions && lastAssistantMessage
      ? AgentTools.buildPendingActionRenderList(
          pendingDeferredToolQueue,
          lastAssistantMessage.id,
          deferredToolPresenter,
        )
      : [];

  const scrollToBottom = useCallback((behavior?: ScrollBehavior) => {
    scrollViewRef.current?.scrollTo({
      top: scrollViewRef.current.scrollHeight,
      behavior: behavior ?? "smooth",
    });
  }, []);

  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages, scrollToBottom]);

  if (isLoading) {
    return null;
  }

  return (
    <div className={cn("flex h-full flex-col", className)}>
      {/* Scrollable messages area */}
      <div ref={scrollViewRef} className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        {[...messages].reverse().map((message) => (
          <SessionMessage<TMessage>
            key={message.id}
            message={message}
            renderUIMessage={renderUIMessage}
            onStreamUpdate={() => scrollToBottom("instant")}
          />
        ))}
      </div>

      {/* Fixed bottom section: Pending actions + Input */}
      <div className="mt-auto shrink-0 border-border border-t">
        {/* Pending actions section */}
        {hasRenderablePendingActions && lastAssistantMessage && (
          <div className="max-h-72 overflow-y-auto px-4 py-3">
            {showStop && (
              <div className="mb-3 flex justify-end">
                <InputGroupButton variant="outline" onClick={stop} disabled={!canStop}>
                  <StopCircleIcon className="h-4 w-4" />
                  Stop
                </InputGroupButton>
              </div>
            )}
            {pendingActionRenderList.map(({ key, part, info }) => (
              <div key={key}>{renderPendingAction(part, info)}</div>
            ))}
          </div>
        )}

        {showPendingFallback && (
          <div className="px-4 py-3">
            {showStop && (
              <div className="mb-3 flex justify-end">
                <InputGroupButton variant="outline" onClick={stop} disabled={!canStop}>
                  <StopCircleIcon className="h-4 w-4" />
                  Stop
                </InputGroupButton>
              </div>
            )}
            <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-muted-foreground text-sm">
              Waiting for pending tool output before chat can continue.
            </div>
          </div>
        )}

        {/* Input area - hidden when pending actions exist */}
        {!hasPendingActions && (
          <div className="p-4">
            <InputGroup>
              <InputGroupTextarea
                disabled={disabled}
                placeholder="Type your message..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    submit();
                  }
                }}
              />
              {showStop && (
                <InputGroupAddon align="inline-end">
                  <InputGroupButton variant="outline" onClick={stop} disabled={!canStop}>
                    <StopCircleIcon className="h-4 w-4" />
                    Stop
                  </InputGroupButton>
                </InputGroupAddon>
              )}
            </InputGroup>
          </div>
        )}
      </div>
    </div>
  );
}

function SessionMessage<TMessage extends UIMessage>({
  message,
  renderUIMessage,
  onStreamUpdate,
}: {
  message: AgentChat.MessagesState["messages"][number];
  renderUIMessage: (message: AgentChat.UIMessageWithStatus<TMessage>) => ReactNode;
  onStreamUpdate?: () => void;
}) {
  const { liveMessage, shouldShowStreamingIndicator } = AgentChat.useMessageState<TMessage>({
    message,
    onStreamUpdate,
  });

  return (
    <>
      {renderUIMessage(liveMessage)}
      {shouldShowStreamingIndicator ? <StreamingIndicator /> : null}
    </>
  );
}
